import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";
import Tooltip from "@material-ui/core/Tooltip";
import {
  DATA_TYPES,
  FONTS,
  GRAY,
  PRIMARY_COLOR,
  PRIMARY_COLOR_RGB,
  SECONDARY_COLOR,
  SECONDARY_COLOR_RGB,
} from "../../constants";
import { formatNumber } from "../../helpers/FormatNumber";
import { getPearson } from "../../helpers/pearsonCorrelation";
import Dataset from "../../MachineLearning/Dataset";
import { stateType, stateTypeF } from "../../state";

const chartWidth = 250;
const posColor = PRIMARY_COLOR_RGB;
const negColor = SECONDARY_COLOR_RGB;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      display: "flex",
      flexDirection: "column",
      width: "100%",
      height: "100%",
    },
    row: {
      display: "flex",
      flexDirection: "row",
      width: "100%",
    },
    rowWithName: {
      display: "flex",
      flexDirection: "row",
      width: "100%",
    },
    rowName: {
      width: "200px",
      textOverflow: "ellipsis",
      overflow: "hidden",
      whiteSpace: "nowrap",
      textAlign: "right",
      fontFamily: FONTS,
    },
    tooltip: { backgroundColor: SECONDARY_COLOR, fontSize: "12px" },
    tooltipArrow: { color: PRIMARY_COLOR },

    hover: {
      transition: ".3s",
      "&:hover": {
        transform: "scale(1.3)",
        backgroundColor: "black",
        zIndex: 1000,
        boxShadow: `0px 0px 2px 1px ${GRAY}`,
      },
    },
    legend: {
      display: "flex",
      flexDirection: "row",
      marginTop: "10px",
      top: "auto",
      width: "100%",
      justifyContent: "center",
      overflow: "hidden",
      marginLeft: "10px",
    },
    legendItem: { display: "flex", flexDirection: "row", marginRight: "20px" },
    plotContainer: { margin: "10px", marginTop: "-5px" },
  })
);

export type correlationType = { correlations: number[][]; colNames: string[] };

export function calcCorrelations(dataset: Dataset): correlationType {
  let colNames: string[] = [];

  for (let col of dataset.columns) {
    if (col.columnType !== DATA_TYPES.unusable) {
      colNames.push(col.columnName);
    }
  }
  colNames = colNames.sort((a, b) =>
    a.toLowerCase() < b.toLowerCase() ? -1 : 1
  );

  const correlations: number[][] = new Array(colNames.length)
    .fill(0)
    .map(() => new Array(colNames.length).fill(0));

  for (let i = 0; i < colNames.length; i++) {
    const colI = dataset.getColumn(colNames[i]).vectorizedData;
    for (let j = 0; j < colNames.length; j++) {
      const colJ = dataset.getColumn(colNames[j]).vectorizedData;

      if (i === j) {
        correlations[i][j] = 1;
      } else if (i > j) {
        correlations[i][j] = correlations[j][i];
      } else {
        correlations[i][j] = getPearson(colI, colJ);
      }
    }
  }
  const tmp = { correlations: correlations, colNames: colNames };
  return tmp;
}

export function ligthenColor(color: number[], cor: number) {
  const corAbs = Math.abs(cor);

  let colorVals = color.map((v) =>
    Math.round(v + (255 - v) * (1 - corAbs - 0.2))
  );
  return `rgb(${colorVals[0]},${colorVals[1]},${colorVals[2]})`;
}

function CorrelationBox(
  cor: number,
  rowName: string,
  colName: string,
  boxWidth: string,
  boxWidthPrct: string,
  state: stateTypeF,
  classes
) {
  //const alpha = (1 + Math.abs(cor)) / 2;
  let base = cor < 0 ? negColor : posColor;
  let color = ligthenColor(base, Math.abs(cor));
  if (Math.abs(cor) < 0.1) {
    color = "rgb(230,230,230)";
  }

  function handleClick() {
    let tmp = state.saveState.correlations.dualChartSelected;
    tmp[0] = rowName;
    tmp[1] = colName;
    state.saveState.correlations.dualChartSelected = tmp;
    state.setSaveState(state.saveState);
  }

  const tooltipVal: string = `${rowName} and ${colName}: ${formatNumber(cor)}`;
  return (
    <Tooltip
      title={<div style={{ fontSize: "14px" }}>{tooltipVal}</div>}
      arrow
      placement="top"
      classes={{
        tooltip: classes.tooltip,
        arrow: classes.tooltipArrow,
      }}
    >
      <div
        className={classes.hover}
        onClick={handleClick}
        style={{
          margin: "0.5%",
          borderRadius: "3px",
          backgroundColor: color,
          cursor: "pointer",
          width: boxWidthPrct,
          height: boxWidth,
        }}
      ></div>
    </Tooltip>
  );
}

function CorrelationRow(
  row: number[],
  cors: correlationType,
  rowNum: number,
  boxWidth: string,
  boxWidthPrct: string,
  state: stateTypeF,
  classes
) {
  return (
    <div className={classes.row}>
      {row.map((cor, colNum) =>
        CorrelationBox(
          cor,
          cors.colNames[rowNum],
          cors.colNames[colNum],
          boxWidth,
          boxWidthPrct,
          state,
          classes
        )
      )}
    </div>
  );
}

function Legend({ boxWidth }) {
  const classes = useStyles();
  return (
    <div className={classes.legend}>
      <LegendItem
        text={"Negative Cor."}
        color={negColor}
        boxWidth={"22px"}
      ></LegendItem>
      <LegendItem
        text={"Positive Cor."}
        color={posColor}
        boxWidth={"22px"}
      ></LegendItem>
    </div>
  );
}

type legendItemType = { text: string; color: number[]; boxWidth: string };
function LegendItem({ text, color, boxWidth }: legendItemType) {
  const classes = useStyles();
  return (
    <div
      style={{ lineHeight: "25px", fontSize: "13px" }}
      className={classes.legendItem}
    >
      <div
        style={{
          margin: "2px",
          borderRadius: "3px",
          backgroundColor: ligthenColor(color, 0.5),
          width: boxWidth,
          height: boxWidth,
          marginRight: "5px",
        }}
      ></div>
      {text}
    </div>
  );
}

export function CorrelationChart({ state }: stateType) {
  // const cors = calcCorrelations(state);
  // useEffect(() => state.setCorrelations(cors), []);
  const classes = useStyles();
  const cors = state.saveState.correlations.correlations;
  if (cors === undefined) {
    return <div></div>;
  }

  const boxWidth = (1 * chartWidth) / cors.colNames.length + "px";
  const boxWidthPrct = 100 / cors.colNames.length + "%";

  return (
    <div className={classes.container}>
      <div className={classes.plotContainer}>
        {cors.correlations.map((row, i) => (
          <div className={classes.rowWithName}>
            {CorrelationRow(
              row,
              cors,
              i,
              boxWidth,
              boxWidthPrct,
              state,
              classes
            )}
          </div>
        ))}
      </div>
      <Legend boxWidth={boxWidth}></Legend>
    </div>
  );
}
